import actions from './actions';
import getters from './getters';
import mutations from './mutations';

import type { User } from '../event/types';

export type SidebarTab = {
  name: string;
  id: string;
  available: () => boolean;
  hidden?: boolean;
  channel_id?: string;
  showIndicator?: boolean;
  indicatorDetails?: Array<{ user: any; count: number }>;
  loadingstate?: 'loading' | 'loaded' | 'left' | 'loaderror';
};

export type BroadcastSlideDecks = {
  slides: { [k: string]: Deck };
  onsidebar: string;
};

export interface Asset {
  id: string;
  urls: {
    pdf: string;
    config: string;
    hls?: string;
  };
  created_at: string;
  updated_at: string;
  name: string;
  uploaded: boolean;
  duration: null | number;
  thumbnail?: string;
  asset_type: string;
  creation_type: string;
  transcode_status: 'SUBMITTED' | 'PROGRESSING' | 'COMPLETE' | 'ERROR';
  transcoder_errors: string[];
  transcoder_warnings: string[];
  organization: string;
  embedded_url: string;
  hls_path?: string;
  fallback_hls_path?: string;
  client_hls_path?: string;
  is_animations_enabled?: boolean;
  path?: string;
  slide_conversion_status?: 'PROGRESSING' | 'COMPLETE' | 'ERROR';
}
export interface Slide {
  thumb: string;
  low: string;
  high: string;
  speaker_notes?: string;
}

export interface QuickEmoji {
  id: string;
  skinTone?: number;
}

export interface Deck {
  id: string;
  name: string;
  pdfUrl: string;
  configUrl: string;
  thumbnail: string;
  numPages: number;
  slides: Slide[];
  transcodeStatus?: Asset['transcode_status'];
  embeddedUrl: string;
  hasSpeakerNotes: boolean;
}

export interface AnimatedSlide {
  slideNumber: number;
  thumb: string;
  high: string;
  speakerNotes?: string;
}

export interface AnimatedDeck {
  id: string;
  name: string;
  path: string | undefined;
  cdnBaseUrl: string;
  thumbnail: string;
  numSlides: number;
  slides: AnimatedSlide[];
  transcodeStatus?: Asset['transcode_status'];
  hasSpeakerNotes: boolean;
  is_animations_enabled: boolean;
}

export interface AnimatedSlideDecks {
  decks: Record<string, AnimatedDeck>;
  onsidebar: string;
}

export interface AnimatedSlidesState {
  slideIndex: number;
  stepNumber: number;
  slidesCount: number;
  currentSlideInput: [number, number];
  slide_state_version: number;
  notes?: string[];
  action?: string;
  maxStepCount?: number;
  slideStepMap?: Record<number, number>;
}

export enum ToolbarTabs {
  video = 'videos',
  slides = 'slides',
  screenShare = 'screenShare',
  layout = 'layout',
  overlay = 'overlay',
  ticker = 'ticker',
  logo = 'logo',
  surveys = 'surveys',
  polls = 'polls',
  textQnA = 'textQnA',
  videoQnA = 'videoQnA',
  runOfShow = 'runOfShow',
  theme = 'theme',
  style = 'style',
  profile = 'profile',
  help = 'help',
  background = 'background',
  empty = ''
}

export type SidebarState = {
  selectedPrimaryTab:
    | ''
    | 'stage_chat'
    | 'messages'
    | 'poll'
    | 'docs'
    | 'assets'
    | 'messages_chat'
    | 'kudo'
    | 'leaderboard'
    | 'vcapture';
  selectedToolbarTab: ToolbarTabs;
  selectedLiveSubTab: string;
  selectedQnaTab: string;
  messagesSearchFilter: string;
  messageList: {
    current: number;
  };
  stageChatSubTabIndex: number;
  slideDecks: BroadcastSlideDecks;
  assetsSubTabIndex: number;
  dmsInitialized: boolean;
  speakerProfile?: User;
  showSpeakerChatDescription: boolean;
  timesWaitedForDMs: number;
  currentDirectChatId: string | null;
  quickEmojis;
  rsSlidesCanvas?: {
    canvasElement: HTMLCanvasElement;
    canvasElementCache: Map<string, HTMLImageElement>;
    slidesCanvasInterval?: NodeJS.Timeout;
    animationFrameId?: number;
    unloadHandler?: () => void;
  };
  isPublishingRSSlides?: boolean;
  animatedSlides: AnimatedSlidesState;
  animatedSlideDecks: AnimatedSlideDecks;
};

function defaultState(): SidebarState {
  return {
    selectedPrimaryTab: '',
    selectedToolbarTab: ToolbarTabs.empty,
    selectedLiveSubTab: '',
    selectedQnaTab: '',
    messagesSearchFilter: '',
    messageList: {
      current: 1
    },
    stageChatSubTabIndex: 0,
    slideDecks: {
      slides: {},
      onsidebar: ''
    },
    assetsSubTabIndex: 0,
    dmsInitialized: false,
    speakerProfile: undefined,
    showSpeakerChatDescription: true,
    timesWaitedForDMs: 0,
    currentDirectChatId: '',
    quickEmojis: [{ id: '+1' }, { id: 'heart' }, { id: 'tada' }],
    animatedSlides: {
      slideIndex: 0,
      stepNumber: 0,
      slidesCount: 0,
      currentSlideInput: [0, 0],
      slide_state_version: 0,
      maxStepCount: 0
    },
    animatedSlideDecks: {
      decks: {},
      onsidebar: ''
    }
  };
}

export default {
  state: defaultState(),
  getters,
  actions,
  mutations,
  namespaced: true
};
